import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { z } from 'zod';

import type { CardModalPayload, ModalProps } from '../types/modal.types';

import { useActivateCardMutation, useGetUserQuery } from '../../__generated__/graphql/api';
import { getUserRefetchQuery } from '../../components/UserDetails/getUserRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';

const schema = z.object({
  referenceId: z.string().min(1, 'Reference ID is required'),
});

export const ActivateCardComponent = ({ payload, api }: ModalProps<CardModalPayload>) => {
  const { userId = '', cardReferenceId } = payload;

  const { data } = useGetUserQuery({
    variables: {
      userId,
    },
    skip: !userId || !!cardReferenceId,
  });

  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getUserRefetchQuery(userId);
  const [activateCard, { loading }] = useActivateCardMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        activateCard({
          variables: {
            referenceId: data.referenceId,
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'activate-card',
      toast: {
        success: 'Card activated successfully',
        error: 'Failed to activate card',
        loading: 'Activating card...',
      },
    });
  };

  const user = data?.user;
  const activeCard = user?.cards?.find((card) => card?.active);
  const referenceId = cardReferenceId ?? activeCard?.referenceId ?? '';

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      cardReferenceId
        ? {
            name: 'referenceId',
            label: '',
            type: 'text',
            defaultValue: referenceId,
            componentProps: {
              wrapperClassName: 'hidden',
              inputClassName: 'hidden',
            },
          }
        : {
            name: 'referenceId',
            label: 'Card Reference ID',
            type: 'text',
            required: true,
            placeholder: 'Enter card reference ID',
          },
    ],
    schema,
    defaultValues: {
      referenceId,
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
